import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';

const SESSION_KEY = 'glp1_exit_popup_shown';
const OPTIN_KEY = 'starter_kit_submitted';

const GLP1_SLUGS = ['ozempic', 'semaglutide', 'tirzepatide', 'glp-1', 'glp1', 'weight-loss'];

const Glp1ExitPopup = ({ slug }) => {
  const [visible, setVisible] = useState(false);
  const navigate = useNavigate();

  // Check if this is a GLP-1 post
  const isGlp1 = slug && GLP1_SLUGS.some(k => slug.toLowerCase().includes(k));

  const show = useCallback(() => {
    if (sessionStorage.getItem(SESSION_KEY)) return;
    if (sessionStorage.getItem(OPTIN_KEY)) return;
    sessionStorage.setItem(SESSION_KEY, '1');
    setVisible(true);
  }, []);

  useEffect(() => {
    if (!isGlp1) return;

    let armed = false;
    const armTimer = setTimeout(() => { armed = true; }, 2000);

    // Desktop: mouse leaves toward top
    const onMouseLeave = (e) => {
      if (armed && e.clientY <= 5) show();
    };
    document.addEventListener('mouseleave', onMouseLeave);

    // Mobile: 40% scroll depth (touch device OR narrow viewport)
    const isMobile = 'ontouchstart' in window || window.innerWidth < 768;
    let scrollFired = false;
    const onScroll = () => {
      if (scrollFired || !armed) return;
      const scrollPct = window.scrollY / (document.body.scrollHeight - window.innerHeight);
      if (scrollPct >= 0.4) {
        scrollFired = true;
        show();
      }
    };
    if (isMobile) {
      window.addEventListener('scroll', onScroll, { passive: true });
    }

    return () => {
      clearTimeout(armTimer);
      document.removeEventListener('mouseleave', onMouseLeave);
      if (isMobile) window.removeEventListener('scroll', onScroll);
    };
  }, [isGlp1, show]);

  if (!isGlp1) return null;

  const close = () => setVisible(false);
  const goKit = () => { close(); navigate('/free-starter-kit'); };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          data-testid="glp1-exit-overlay"
          onClick={close}
        >
          <div className="absolute inset-0 bg-black/70" />

          <motion.div
            initial={{ opacity: 0, scale: 0.92, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 8 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md rounded-lg overflow-hidden"
            style={{ background: '#0B1F3A', border: '1px solid rgba(42,191,191,0.25)' }}
            data-testid="glp1-exit-popup"
          >
            {/* Close */}
            <button
              onClick={close}
              className="absolute top-3 right-3 text-slate-500 hover:text-white transition-colors z-10"
              data-testid="glp1-exit-close"
            >
              <X className="h-5 w-5" />
            </button>

            <div className="p-8 sm:p-10 text-center">
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2 leading-tight">
                Before You Go...
              </h2>
              <p className="text-lg font-semibold mb-4" style={{ color: '#2ABFBF' }}>
                Grab the Free GLP-1 Starter Kit
              </p>
              <p className="text-sm leading-relaxed mb-8" style={{ color: '#C8E8E8' }}>
                11 pages covering medication comparison, what to eat, side effects, and how to maximize your results. Free — no credit card needed.
              </p>

              <button
                onClick={goKit}
                data-testid="glp1-exit-cta"
                className="w-full py-4 rounded-full font-bold text-base transition-colors"
                style={{ background: '#2ABFBF', color: '#0B1F3A' }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#24a8a8'}
                onMouseLeave={(e) => e.currentTarget.style.background = '#2ABFBF'}
              >
                Download Free →
              </button>

              <p className="text-xs mt-4" style={{ color: '#5a8a8a' }}>
                Join others already using this guide.
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default Glp1ExitPopup;
